import { DeleteOutline } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import {
    ReactNode,
    forwardRef,
    useCallback,
    useImperativeHandle,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { catalogItemActions } from 'entities/CatalogItem/model/slices/CatalogItemSlice';
import { fetchOrder } from 'entities/Order';
import {
    getIsOrderSubmissionLocked,
    getSelectedOrderId,
} from 'entities/Order/model/selectors/ordersSelectors';
import { deleteOrderItem } from 'features/removeItemsInOrder/model/services/deleteOrderItem/deleteOrderItem';
import { getRouteNotFound, getRouteOrdersView } from 'shared/const/router';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import RemoveOrderModal from 'shared/ui/RemoveOrderModal/RemoveOrderModal';

interface RemoveItemFromOrderProps {
    itemId: string;
    showConfirmationDialog?: boolean;
    renderButton?: (onClick: () => void) => ReactNode;
}

/**
 * Ref interface for programmatically triggering the delete dialog
 */
export interface RemoveItemFromOrderRef {
    triggerDelete: () => void;
}

/**
 * Delete item component with confirmation dialog.
 * Supports custom button via render props and ref-based triggering.
 */
export const RemoveItemFromOrder = forwardRef<
    RemoveItemFromOrderRef,
    RemoveItemFromOrderProps
>(({ itemId, renderButton, showConfirmationDialog = true }, ref) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const selectedOrderId = useSelector(getSelectedOrderId);
    const isOrderSubmissionLocked = useSelector(getIsOrderSubmissionLocked);

    const [showDialog, setShowDialog] = useState(false);
    const [loading, setLoading] = useState(false);

    const closeDialog = useCallback(() => {
        setShowDialog(false);
    }, []);

    const redirectOnNotFound = useCallback(
        () => navigate(getRouteNotFound()),
        [navigate],
    );

    const submitDialog = useCallback(async () => {
        setLoading(true);
        const params = {
            orderId: selectedOrderId,
            itemId: itemId,
            qty: 0,
        };
        await dispatch(deleteOrderItem(params));
        await dispatch(
            fetchOrder({
                id: selectedOrderId,
                onNotFound: redirectOnNotFound,
            }),
        );

        navigate(getRouteOrdersView(selectedOrderId));
        dispatch(catalogItemActions.removeItem(itemId));
        setShowDialog(false);
        setLoading(false);
    }, [dispatch, itemId, navigate, redirectOnNotFound, selectedOrderId]);

    const openDialog = useCallback(() => {
        if (isOrderSubmissionLocked) return;
        if (showConfirmationDialog) {
            setShowDialog(true);
        } else {
            submitDialog();
        }
    }, [showConfirmationDialog, submitDialog, isOrderSubmissionLocked]);

    useImperativeHandle(
        ref,
        () => ({
            triggerDelete: openDialog,
        }),
        [openDialog],
    );

    return (
        <Box>
            {renderButton ? (
                renderButton(openDialog)
            ) : (
                <Button
                    disabled={isOrderSubmissionLocked}
                    sx={{
                        p: '6px',
                        minWidth: '25px',
                        '& .MuiButton-startIcon': {
                            margin: 0,
                            color: 'rgba(0, 0, 0, 0.30)',
                        },
                        '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            '& .MuiButton-startIcon': {
                                color: 'rgba(0, 0, 0, 0.50)',
                            },
                        },
                    }}
                    startIcon={
                        <DeleteOutline
                            sx={{
                                fontSize: '25px !important',
                            }}
                        />
                    }
                    onClick={openDialog}
                    data-testid="btn-order-item-delete"
                />
            )}
            <RemoveOrderModal
                title={t('Delete Item from the Order')}
                text={t('You are about to remove this item from an order.')}
                deleteButtonText={`Delete item`}
                isShowDeleteOrder={showDialog}
                isLoadingDeleteOrder={loading}
                onCloseDeleteOrder={closeDialog}
                onSubmitDeleteOrder={submitDialog}
            />
        </Box>
    );
});

RemoveItemFromOrder.displayName = 'RemoveItemFromOrder';
